import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { Col, ConfigProvider, Divider, Row, Select } from 'antd';
import { Flex, LoadingSpin, PageContainer, RetryAlert } from '@/components';
import { useDevice, useDormitoryGroupManagement, useDormitoryRequests, useDormitorySolitaryBlocks, useDormitorySolitaryFloors, useValidEntranceDormitory } from '@/hooks';
import ErrorNoValidPreRegister from '../ErrorNoValidPreRegister';
import FloorsList from './FloorsList';


export default function DormitoryRegisterSolitarySelectRoom() {

  const { isDesktop } = useDevice();
  const { group, fetchGroup, fetching } = useDormitoryGroupManagement();
  const { validEntrance, loading: entranceLoading, fetchValidEntrance } = useValidEntranceDormitory(true);
  const { findRequestBySemesters, error: requestsError, fetchDormitoryRequests, loading: requestLoading } = useDormitoryRequests(true);
  const { fetchBlocks, blocks, blocksError, blocksLoading } = useDormitorySolitaryBlocks();
  const { fetchFloors, floors, floorsError, floorsLoading } = useDormitorySolitaryFloors();
  const [selectedBlock, setSelectedBlock] = useState<number>(null);

  const [t] = useTranslation(['common', 'dormitory']);
  const validRequest = findRequestBySemesters(validEntrance?.semesters, true);

  const fetchCurrentDormitoryBlocks = useCallback(() => {
    if (!validRequest) return;
    fetchBlocks(validRequest?.destinationDormitory?.id);
  }, [validRequest, fetchBlocks]);

  const fetchCurrentBlockFloors = useCallback(() => {
    if (!validRequest || !selectedBlock) return;
    fetchFloors(validRequest?.destinationDormitory?.id, selectedBlock);
  }, [validRequest, selectedBlock, fetchFloors]);

  useEffect(() => {
    const block = group?.dormitoryRoom?.dormitory?.dormitoryBlock;
    if (block) {
      setSelectedBlock(block.id);
    }
  }, [group]);

  useEffect(() => {
    fetchValidEntrance();
    fetchGroup();
    fetchDormitoryRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchCurrentDormitoryBlocks();
  }, [fetchCurrentDormitoryBlocks]);

  useEffect(() => {
    fetchCurrentBlockFloors();
  }, [fetchCurrentBlockFloors]);

  if (requestsError) {
    return (
      <Flex align="center" className="h-full pb-20">
        <RetryAlert noIcon content={requestsError} action={fetchDormitoryRequests} />
      </Flex>
    );
  }

  if (blocksError) {
    return (
      <Flex align="center" className="h-full pb-20">
        <RetryAlert noIcon content={blocksError} action={fetchCurrentDormitoryBlocks} />
      </Flex>
    );
  }

  if (floorsError) {
    return (
      <Flex align="center" className="h-full pb-20">
        <RetryAlert noIcon content={floorsError} action={fetchCurrentBlockFloors} />
      </Flex>
    );
  }

  const loading = fetching || requestLoading || entranceLoading;

  if (!loading && !validRequest) return <ErrorNoValidPreRegister />;

  return (
    <PageContainer title={t('dormitory:labels.select-room')}>
      <ConfigProvider componentSize={isDesktop() ? 'large' : 'middle'}>
        <Row justify="center">
          <Col span={24} xl={17}>
            <Flex direction="column" align="center" className="w-full h-full pb-20">
              {loading ? <LoadingSpin fillHeight /> : (
                <>
                  <Row justify="start" className="w-full">
                    <Col span={24}>
                      <Flex direction="row" align="center">
                        <Flex justify="flex-start" className="text-lg font-bold">
                          <span>{t('dormitory:labels.dormitory')}</span>
                          <span>{validRequest?.destinationDormitory?.name}</span>
                        </Flex>
                      </Flex>
                      <Divider />
                    </Col>
                  </Row>
                  <Row className="w-full pt-4" justify="center">
                    <Col xxl={8} xl={10} lg={12} md={14} xs={24} span={24}>
                      <Select
                        size="middle"
                        className="w-full"
                        loading={blocksLoading}
                        style={{ height: '40px' }}
                        placeholder={t('dormitory:messages.select-desired-block')}
                        value={selectedBlock != null ? String(selectedBlock) as any : undefined}
                        onChange={(value) => setSelectedBlock(value)}
                        options={blocks.map(block => ({ label: block.name, value: block.id }))}
                      />
                    </Col>
                  </Row>
                </>
              )}
              {!!selectedBlock && (
                <Row className="w-full pt-4" justify="center">
                  <Col span={24}>
                    <Flex className={classNames('rounded-lg', { 'min-h-[200px]': floorsLoading })}>
                      {floorsLoading ? <LoadingSpin /> : (
                        <Flex className="w-full overflow-auto" direction="column">
                          {floors.length > 0 ?
                            <FloorsList
                              floors={floors}
                              group={group}
                              blockId={selectedBlock}
                              showMembersEnabled={true}
                              dormitory={validRequest.destinationDormitory}
                              actionMode={group == null ? 'create' : 'update'}
                            /> :
                            <div className="m-auto text-center text-red-500">
                              {t('dormitory:errors.not-eligible-in-dormitory')}
                            </div>
                          }
                        </Flex>
                      )}
                    </Flex>
                  </Col>
                </Row>
              )}
            </Flex>
          </Col>
        </Row>
      </ConfigProvider>
    </PageContainer>
  );
}
